import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice
} from '@reduxjs/toolkit';

// Utils
import { ORDER_STATUS } from 'utils/orders/constants';
import { supabase } from 'utils/supabase-client';

import { clearUserData } from './userSlice';

// How far back the board fetches orders (keeps terminal columns bounded).
// Shared select shape (snake_case columns aliased to camelCase).
const ORDERS_SELECT = `
  id,
  createdAt: created_at,
  updatedAt: updated_at,
  status,
  customerName: customer_name,
  customerEmail: customer_email,
  customerPhone: customer_phone,
  notes,
  denialReason: denial_reason,
  proposedItems: proposed_items,
  proposedTotal: proposed_total,
  adjustmentNote: adjustment_note,
  pickupDatetime: pickup_datetime,
  totalPrice: total_price,
  restaurantAddress: restaurant_address,
  restaurantAddressLabel: restaurant_address_label,
  orderItems: order_items!order_items_order_id_fkey (
    quantity,
    product: products!order_items_product_id_fkey (
      id,
      price,
      translations: products_translations ( locale, name )
    )
  )
`;

// Adapter
const ordersAdapter = createEntityAdapter({
  selectId: order => order.id
});

const additionalInitialStateOptions = {
  isFetchingOrders: false,
  receivedTodayCount: 0
};

// Thunks
const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async ({ dayEnd, dayStart, restaurantId }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(ORDERS_SELECT)
        .eq('restaurant_id', restaurantId)
        .gte('pickup_datetime', dayStart)
        .lt('pickup_datetime', dayEnd)
        .order('pickup_datetime', { ascending: true, nullsFirst: false });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (orderId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(ORDERS_SELECT)
        .eq('id', orderId)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const fetchReceivedTodayCount = createAsyncThunk(
  'orders/fetchReceivedTodayCount',
  async (restaurantId, { rejectWithValue }) => {
    try {
      const dayStart = new Date();

      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(dayStart);

      dayEnd.setDate(dayEnd.getDate() + 1);

      const { count, error } = await supabase
        .from('orders')
        .select('id', { count: 'exact', head: true })
        .eq('restaurant_id', restaurantId)
        .eq('status', ORDER_STATUS.RECEIVED)
        .gte('pickup_datetime', dayStart.toISOString())
        .lt('pickup_datetime', dayEnd.toISOString());

      if (error) {
        throw error;
      }

      return count ?? 0;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const updateOrderStatus = createAsyncThunk(
  'orders/updateOrderStatus',
  async ({ denialReason, notifyCustomer = true, orderId, status }, { rejectWithValue }) => {
    try {
      const changes = { status };

      // Only touch denial_reason when a value was explicitly provided (deny
      // flow). The column-scoped UPDATE grant covers status + denial_reason.
      if (typeof denialReason !== 'undefined') {
        // eslint-disable-next-line camelcase
        changes.denial_reason = denialReason || null;
      }

      const { data, error } = await supabase
        .from('orders')
        .update(changes)
        .eq('id', orderId)
        .select('id, status, denialReason:denial_reason, updatedAt:updated_at')
        .single();

      if (error) {
        throw error;
      }

      // Fire-and-forget customer notification. The edge function looks up
      // the order with the caller's session (RLS gates ownership) and sends
      // the email via Loops. Errors are swallowed so an email failure never
      // blocks the status update from succeeding. Skipped for past orders
      // (the kitchen is just reconciling history — the customer shouldn't be
      // pinged about it).
      if (notifyCustomer) {
        supabase.functions.invoke('notify-order-status-change', {
          body: { orderId }
        }).catch((notifyError) => {
          // eslint-disable-next-line no-console
          console.error('Failed to dispatch order status notification:', notifyError);
        });
      }

      return data;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const adjustOrder = createAsyncThunk(
  'orders/adjustOrder',
  async ({ items, note, orderId, total }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.functions.invoke('adjust-order', {
        body: { items, note, orderId, total }
      });

      if (error || data?.error) {
        throw error || new Error(data.error);
      }

      return { orderId };
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

// Slice
const ordersSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchOrders.pending, (state) => {
        state.isFetchingOrders = true;
      })
      .addCase(fetchOrders.fulfilled, (state, { payload }) => {
        state.isFetchingOrders = false;

        if (Array.isArray(payload)) {
          ordersAdapter.setAll(state, payload);
        }
      })
      .addCase(fetchOrders.rejected, (state) => {
        state.isFetchingOrders = false;
      })
      // Fetch one (used by realtime INSERT)
      .addCase(fetchOrderById.fulfilled, (state, { payload }) => {
        if (payload) {
          ordersAdapter.upsertOne(state, payload);
        }
      })
      // Sidebar's "today's received" badge count
      .addCase(fetchReceivedTodayCount.fulfilled, (state, { payload }) => {
        state.receivedTodayCount = typeof payload === 'number' ? payload : 0;
      })
      // Optimistic status update
      .addCase(updateOrderStatus.pending, (state, { meta }) => {
        const changes = { status: meta.arg.status };

        if (typeof meta.arg.denialReason !== 'undefined') {
          changes.denialReason = meta.arg.denialReason || null;
        }

        ordersAdapter.updateOne(state, {
          changes,
          id: meta.arg.orderId
        });
      })
      .addCase(updateOrderStatus.fulfilled, (state, { payload }) => {
        if (payload) {
          ordersAdapter.upsertOne(state, payload);
        }
      })
      .addCase(updateOrderStatus.rejected, (state, { meta }) => {
        // Only roll back when we have a valid order and a known previous
        // status; otherwise we'd overwrite status with undefined.
        if (meta.arg.orderId && meta.arg.previousStatus != null) {
          const changes = { status: meta.arg.previousStatus };

          // Restore denial_reason too when it was part of the optimistic
          // change (mirrors the pending branch), so a failed deny doesn't
          // leave the typed reason behind.
          if (typeof meta.arg.denialReason !== 'undefined') {
            changes.denialReason = meta.arg.previousDenialReason ?? null;
          }

          ordersAdapter.updateOne(state, {
            changes,
            id: meta.arg.orderId
          });
        }
      })
      // Reset on logout
      .addCase(clearUserData, () => ({
        ...ordersAdapter.getInitialState(additionalInitialStateOptions)
      }));
  },

  initialState: ordersAdapter.getInitialState(additionalInitialStateOptions),

  name: 'orders',

  reducers: {
    removeOrder: (state, { payload }) => {
      ordersAdapter.removeOne(state, payload);
    },
    upsertOrder: (state, { payload }) => {
      if (payload) {
        ordersAdapter.upsertOne(state, payload);
      }
    }
  }
});

// Selectors
const ordersSelectors = ordersAdapter.getSelectors(state => state.orders);

const { selectAll: selectOrders } = ordersSelectors;

const selectIsFetchingOrders = state => state.orders.isFetchingOrders;

const selectOrderById = id => state => ordersSelectors.selectById(state, id);

const selectReceivedTodayCount = state => state.orders.receivedTodayCount;

const { removeOrder, upsertOrder } = ordersSlice.actions;

export {
  adjustOrder,
  fetchOrderById,
  fetchOrders,
  fetchReceivedTodayCount,
  removeOrder,
  selectIsFetchingOrders,
  selectOrderById,
  selectOrders,
  selectReceivedTodayCount,
  updateOrderStatus,
  upsertOrder
};

export default ordersSlice.reducer;
